import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export type PrefVariant = 'card' | 'section';

/**
 * Shared chrome for the profile preference controls (theme / font / thumbnail
 * size / summary stats). Two looks from one definition:
 *
 *   • 'card'    — standalone Card with header + description (legacy look).
 *   • 'section' — compact titled block (no card, no description) for the
 *                 bundled "Display preferences" card at the bottom of Profile.
 *
 * `action` renders to the right of the title (e.g. a toggle); `children` is the
 * control body below (e.g. a tile grid).
 */
export const PrefSection = ({
  variant = 'card',
  icon: Icon,
  title,
  description,
  action,
  children,
}: {
  variant?: PrefVariant;
  icon: LucideIcon;
  title: string;
  description?: string;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  if (variant === 'section') {
    return (
      <section>
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-medium">{title}</h3>
          </div>
          {action}
        </div>
        {children}
      </section>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </CardTitle>
          {action}
        </div>
      </CardHeader>
      <CardContent>
        {description && <p className="mb-3 text-xs text-muted-foreground">{description}</p>}
        {children}
      </CardContent>
    </Card>
  );
};
